import { authFetch } from '../auth/authFetch';
import {
  peekWorkspaceIdentity,
  redirectToSignIn,
  requireIdentityHeaders,
} from '../auth/identity';
import { WORKSPACE_IDS } from '../auth/workspaces';

const API_BASE_URL =
  (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000').replace(
    /\/$/,
    ''
  );

export const CUSTODIAN_AUTHORITY_DECISIONS = ['APPROVED', 'REJECTED'];

function buildQueryString(query) {
  const params = new URLSearchParams();

  Object.entries(query || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  });

  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
}

function getContextHeaders() {
  return requireIdentityHeaders(WORKSPACE_IDS.CUSTODIAN);
}

function extractErrorMessage(errorPayload, status) {
  const fallback = `Request failed (${status})`;
  if (!errorPayload || typeof errorPayload !== 'object') {
    return fallback;
  }
  if (typeof errorPayload.message === 'string' && errorPayload.message.trim()) {
    return errorPayload.message;
  }
  if (typeof errorPayload.detail === 'string' && errorPayload.detail.trim()) {
    return errorPayload.detail;
  }
  return fallback;
}

function fallbackMessageForStatus(status) {
  if (status === 403) return 'Custodian access required.';
  if (status === 404) {
    return 'Package not found or not available for Custodian review.';
  }
  if (status === 409) {
    return 'Package is not currently eligible for this action.';
  }
  return `Request failed (${status})`;
}

function createRequestError(message, status) {
  const error = new Error(message);
  error.status = status;
  if (status === 403) {
    error.authorizationError = true;
  }
  return error;
}

async function messageFromErrorResponse(response) {
  let message = fallbackMessageForStatus(response.status);
  try {
    const errorPayload = await response.json();
    const backendMessage = extractErrorMessage(errorPayload, response.status);
    if (backendMessage && backendMessage !== `Request failed (${response.status})`) {
      message = backendMessage;
    } else if (response.status === 400) {
      message = backendMessage;
    }
  } catch {
    // Keep status fallback when body is empty or not JSON.
  }
  if (response.status === 400 && message === `Request failed (${response.status})`) {
    message = fallbackMessageForStatus(response.status);
  }
  return message;
}

async function parseResponse(response) {
  if (!response.ok) {
    if (response.status === 401) redirectToSignIn();
    throw createRequestError(await messageFromErrorResponse(response), response.status);
  }

  if (response.status === 204) {
    return null;
  }
  return response.json();
}

async function request(path, { method = 'GET', body, query, headers } = {}) {
  const response = await authFetch(`${API_BASE_URL}${path}${buildQueryString(query)}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...getContextHeaders(),
      ...(headers || {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  return parseResponse(response);
}

export function getCustodianApiConfig() {
  const identity = peekWorkspaceIdentity(WORKSPACE_IDS.CUSTODIAN);
  return {
    apiBaseUrl: API_BASE_URL,
    userId: identity.userId,
    orgId: identity.orgId,
  };
}

export function getCustodianAuthorityReview({ page = 1, pageSize = 20 } = {}) {
  return request('/v1/custodian/authority-review', {
    query: {
      page,
      page_size: pageSize,
    },
  });
}

export function getCustodianPendingAdmission({ page = 1, pageSize = 50 } = {}) {
  return request('/v1/custodian/pending-admission', {
    query: {
      page,
      page_size: pageSize,
    },
  });
}

export function listCustodianVaultAssets({ page = 1, pageSize = 50 } = {}) {
  return request('/v1/custodian/vault-assets', {
    query: {
      page,
      page_size: pageSize,
    },
  });
}

export function getCustodianPackage(packageId) {
  return request(`/v1/custodian/packages/${packageId}`);
}

export function submitCustodianAuthorityDecision(packageId, decision) {
  return request(`/v1/custodian/packages/${packageId}/authority-decision`, {
    method: 'POST',
    body: {
      decision,
    },
  });
}

export function admitCustodianPackage(packageId) {
  return request(`/v1/custodian/packages/${packageId}/admit`, {
    method: 'POST',
  });
}

export function getCustodianAdmissionStatus(packageId) {
  return request(`/v1/custodian/packages/${packageId}/admission-status`);
}

export function listCustodianCopyRequests({ page = 1, pageSize = 50 } = {}) {
  return request('/v1/custodian/copy-requests', {
    query: { page, page_size: pageSize },
  });
}

export function issueCustodianCertifiedCopy(requestId) {
  return request(`/v1/custodian/copy-requests/${requestId}/issue`, {
    method: 'POST',
  });
}

export function listCustodianIssuedCopies({ page = 1, pageSize = 50 } = {}) {
  return request('/v1/custodian/copies', {
    query: { page, page_size: pageSize },
  });
}

export function getCustodianPackageCopyContexts(packageId) {
  return request(`/v1/custodian/packages/${packageId}/copy-contexts`);
}

export function getCustodianFileCopyContext(packageId, fileId) {
  return request(`/v1/custodian/packages/${packageId}/files/${fileId}/copy-context`);
}

async function requestBlob(path, { query } = {}) {
  const response = await authFetch(`${API_BASE_URL}${path}${buildQueryString(query)}`, {
    method: 'GET',
    headers: {
      ...getContextHeaders(),
    },
  });
  if (!response.ok) {
    if (response.status === 401) redirectToSignIn();
    throw createRequestError(await messageFromErrorResponse(response), response.status);
  }
  const blob = await response.blob();
  const headerType = response.headers.get('content-type');
  const mimeType = (headerType || blob.type || 'application/octet-stream').split(';')[0].trim();
  return { blob, mimeType };
}

export function downloadCustodianIssuedCopy(copyId) {
  return requestBlob(`/v1/custodian/copies/${copyId}/content`);
}

export function downloadCustodianFileRendition(packageId, fileId, { disposition = 'inline' } = {}) {
  return requestBlob(`/v1/custodian/packages/${packageId}/files/${fileId}/rendition`, {
    query: { disposition },
  });
}

export function downloadCustodianOriginal(packageId, fileId) {
  return requestBlob(`/v1/custodian/packages/${packageId}/files/${fileId}/original`);
}
